import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { CONTACT_INFO, siteImages } from '../data/mock';
import { Truck, Shield, Clock, Package, MapPin, Dumbbell } from 'lucide-react';

const ShippingPage = () => {
  useEffect(() => {
    document.title = 'Shipping Information | Sport Fitt Pro';
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <CartDrawer />
      
      <main className="pt-[104px]">
        {/* Banner Section */}
        <section className="relative bg-white border-b border-ink-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-athletic-500/10 border border-athletic-500/30 rounded-full text-athletic-500 text-xs font-semibold mb-4">
              <Truck className="w-3.5 h-3.5" />
              DELIVERY INFO
            </div>
            <h1 className="font-display text-4xl md:text-5xl text-white font-extrabold mb-3">
              Shipping Information
            </h1>
            <p className="text-white/70 max-w-lg">
              Fast, reliable shipping across the United States
            </p>
          </div>
        </section>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Shipping Highlights */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white border border-ink-100 rounded-2xl p-6 text-center">
              <div className="w-14 h-14 bg-athletic-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Truck className="w-7 h-7 text-athletic-500" />
              </div>
              <h3 className="font-display font-bold text-white text-lg mb-2">Free Shipping</h3>
              <p className="text-ink-500 text-sm">On orders over $75</p>
            </div>
            <div className="bg-white border border-ink-100 rounded-2xl p-6 text-center">
              <div className="w-14 h-14 bg-athletic-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Clock className="w-7 h-7 text-athletic-500" />
              </div>
              <h3 className="font-display font-bold text-white text-lg mb-2">3-5 Business Days</h3>
              <p className="text-ink-500 text-sm">Standard delivery time</p>
            </div>
            <div className="bg-white border border-ink-100 rounded-2xl p-6 text-center">
              <div className="w-14 h-14 bg-athletic-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Package className="w-7 h-7 text-athletic-500" />
              </div>
              <h3 className="font-display font-bold text-white text-lg mb-2">Order Tracking</h3>
              <p className="text-ink-500 text-sm">Track your shipment</p>
            </div>
          </div>

          {/* Shipping Details */}
          <div className="bg-white border border-ink-100 rounded-2xl p-8 md:p-12">
            <h2 className="font-display text-2xl font-bold text-ink-900 mb-6">Shipping Policy</h2>
            
            <div className="space-y-8">
              <div>
                <h3 className="font-display font-bold text-white text-lg mb-3">Shipping Rates</h3>
                <div className="bg-ink-50 rounded-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-ink-200">
                        <th className="text-left p-4 text-ink-500 font-semibold">Order Total</th>
                        <th className="text-left p-4 text-ink-500 font-semibold">Shipping Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-ink-200">
                        <td className="p-4 text-white">Under $75</td>
                        <td className="p-4 text-white">$5.99</td>
                      </tr>
                      <tr>
                        <td className="p-4 text-white">$75 and above</td>
                        <td className="p-4 text-athletic-500 font-semibold">FREE</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <h3 className="font-display font-bold text-white text-lg mb-3">Delivery Times</h3>
                <p className="text-ink-600 mb-4">
                  Standard shipping typically takes 3-5 business days from the date of shipment. Please allow 1-2 business days for order processing before shipment.
                </p>
                <ul className="text-ink-600 space-y-2 list-disc list-inside">
                  <li>Orders placed before 2 PM EST ship same day</li>
                  <li>Orders placed after 2 PM EST ship next business day</li>
                  <li>No deliveries on weekends or holidays</li>
                </ul>
              </div>

              <div>
                <h3 className="font-display font-bold text-white text-lg mb-3">Shipping Carrier</h3>
                <p className="text-ink-600">
                  We ship all orders via USPS (United States Postal Service). You will receive a tracking number via email once your order ships.
                </p>
              </div>

              <div>
                <h3 className="font-display font-bold text-white text-lg mb-3">Shipping Restrictions</h3>
                <p className="text-ink-600">
                  Currently, we only ship within the United States. We do not ship to P.O. boxes, APO/FPO addresses, or international destinations at this time.
                </p>
              </div>

              <div>
                <h3 className="font-display font-bold text-white text-lg mb-3">Questions?</h3>
                <p className="text-ink-600 mb-4">
                  If you have any questions about shipping, please contact our support team:
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <a 
                    href={`mailto:${CONTACT_INFO.email}`}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-athletic-500 text-ink-900 font-semibold rounded-xl hover:bg-athletic-400 transition-colors"
                  >
                    {CONTACT_INFO.email}
                  </a>
                  <a 
                    href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-ink-50 text-ink-900 font-semibold rounded-xl hover:bg-ink-100 transition-colors border border-ink-200"
                  >
                    {CONTACT_INFO.phone}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default ShippingPage;
